import { forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useColorModeValue } from "@chakra-ui/react";
import ReactMapGl, { type MapRef, type MapProps, ScaleControl} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { LngLatBoundsLike, TransformRequestFunction } from "mapbox-gl";
import MiniMap from "./MiniMap";
import BasemapSwitcher, { BasemapOption } from "./BasemapSwitcher";

const SATELLITE_STYLE_URL = "mapbox://styles/mapbox/satellite-v9";

const transformRequest: TransformRequestFunction = (url, resourceType) => {
  const token = localStorage.getItem("token");
  const { REACT_APP_API_BASE } = process.env;

  if (token && resourceType === "Tile" && REACT_APP_API_BASE && url.startsWith(REACT_APP_API_BASE)) {
    return {
      url,
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    };
  }

  return {
    url
  };
};

type Props = MapProps & {
  bounds?: LngLatBoundsLike;
  boundsPadding?: number;
  zoomDuration?: number;
  miniMap?: boolean;
  basemapSwitcher?: boolean;
}

function Map({bounds, boundsPadding = 30, zoomDuration = 0, miniMap, basemapSwitcher, ...props}: Props, ref: Ref<MapRef | undefined>) {
  const [map, setMap] = useState<MapRef>();
  const setMapRef = (m: MapRef) => setMap(m);

  useImperativeHandle(ref, () => {
    if (map) { return map; }
  }, [map]);


  const defaultMapStyle = useColorModeValue(
    "mapbox://styles/devseed/clz7artka03eh01p9518wgnpy",
    "mapbox://styles/devseed/clz35cbi302l701qo2snhdx9x"
  );

  const [basemapId, setBasemapId] = useState("default");
  const mapStyle = basemapId === "satellite" ? SATELLITE_STYLE_URL : defaultMapStyle;

  const basemapOptions: BasemapOption[] = useMemo(() => [
    { id: "default", name: "Default" },
    { id: "satellite", name: "Satellite" },
  ], []);

  useEffect(() => {
    if (map && bounds) {
      if(bounds[0 as keyof LngLatBoundsLike] < -180 || bounds[0 as keyof LngLatBoundsLike] > 180) return;
      map.fitBounds(bounds, { padding: boundsPadding, duration: zoomDuration });
    }
  }, [map, bounds, boundsPadding, zoomDuration]);

  return (
    <ReactMapGl
      ref={setMapRef}
      mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
      initialViewState={{
        longitude: -122.355,
        latitude: 37.828,
        zoom: 3,
      }}
      mapStyle={mapStyle}
      projection={{
        name: "mercator",
      }}
      transformRequest={transformRequest}
      touchPitch={false}
      dragRotate={false}
      {...props}
    >
      {props.children}
      {miniMap && <MiniMap />}
      {basemapSwitcher && (
        <BasemapSwitcher options={basemapOptions} activeId={basemapId} onChange={setBasemapId} />
      )}
      <ScaleControl />
    </ReactMapGl>
  );
}

export default forwardRef(Map);
